import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import {
    Box,
    Button,
    Checkbox,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import styles from './AdminConfigTractorSprayProfiles.module.css';
import AddSprayProfileDialog from './AddSprayProfileDialog/AddSprayProfileDialog';
import DeleteSprayProfileDialog from './DeleteSprayProfileDialog/DeleteSprayProfileDialog';

const AdminConfigTractorSprayProfiles = () => {
    const [searchParams] = useSearchParams();
    const [tractor, setTractor] = useState({});
    const [sprayProfiles, setSprayProfiles] = useState([]);
    const [fetchedSprayProfiles, setFetchedSprayProfiles] = useState([]);
    const [sprayProfileForDeletion, setSprayProfileForDeletion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(false);
    const [addSprayProfileDialogOpen, setIsAddSprayProfileDialogOpen] = useState(false);
    const [deleteSprayProfileDialogOpen, setIsDeleteSprayProfileDialogOpen] = useState(false);

    const getClientId = useCallback(
        () => searchParams.get('clientId'),
        [searchParams]
    );

    const getTractorId = useCallback(
        () => searchParams.get('tractorId'),
        [searchParams]
    );

    const fetchTractor = useCallback(async () => {
        setLoading(true);
        try {
            const clientId = getClientId();
            const tractorId = getTractorId();
            const token = await authService.getAccessToken();
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/admin/tractor?clientId=${clientId}&tractorId=${tractorId}`,
                {
                    headers: !token ? {} : { Authorization: `Bearer ${token}` }
                }
            );
            const tractor = await response.json();
            setTractor(tractor);
            setLoading(false);
            setError(false);
        } catch (error) {
            setTimeout(() => {
                setError(true);
                setLoading(false);
            }, 1000);
        }
    }, [getClientId, getTractorId]);

    const fetchSprayProfiles = useCallback(async () => {
        setLoading(true);
        try {
            const clientId = getClientId();
            const tractorId = getTractorId();
            const token = await authService.getAccessToken();
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/spray-profiles?clientId=${clientId}&tractorId=${tractorId}`,
                {
                    headers: !token ? {} : { Authorization: `Bearer ${token}` }
                }
            );
            const sprayProfiles = (await response.json()).map((n) => ({
                ...n,
                overlap: Math.round(n.overlap * 100)
            }));
            setFetchedSprayProfiles(sprayProfiles);
            setSprayProfiles(sprayProfiles);
            setLoading(false);
            setError(false);
        } catch (error) {
            setTimeout(() => {
                setError(true);
                setLoading(false);
            }, 1000);
        }
    }, [getClientId, getTractorId]);

    useEffect(() => {
        fetchTractor();
        fetchSprayProfiles();
    }, [getClientId, getTractorId, fetchTractor, fetchSprayProfiles]);

    const onEditSprayProfile = (sprayProfileId, fieldName, newValue) => {
        debugger;
        const updatedSprayProfiles = sprayProfiles.map((sprayProfile) => {
            let value;
            if (sprayProfile.id === sprayProfileId) {
                return {
                    ...sprayProfile,
                    [fieldName]: newValue
                };
            }
            return sprayProfile;
        });
        setSprayProfiles(updatedSprayProfiles);
    };

    const hasSprayProfileValueChanged = (sprayProfile, fieldName) => {
        const fetchedSprayProfile = fetchedSprayProfiles.find(
            (fetchedBoom) => fetchedBoom.id === sprayProfile.id
        );
        return fetchedSprayProfile[fieldName] !== sprayProfile[fieldName];
    };

    const haveAnyValuesChanged = () => {
        return _.isEqual(fetchedSprayProfiles, sprayProfiles);
    };

    const discardChanges = () => {
        setSprayProfiles(fetchedSprayProfiles);
    };

    const isValid = () => {
        const numOfDefaultProfiles = sprayProfiles.filter(profile => profile.isDefault).length

        if (numOfDefaultProfiles > 1) {
            toast.error('There can only be at most 1 default spray profile.');
            return false;
        }
        return true;
    };
    const updateSprayProfiles = async () => {
        if (!isValid()) {
            return;
        }

        const token = await authService.getAccessToken();
        debugger;
        const response = await fetch(
            `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/spray-profiles`,
            {
                method: 'POST',
                headers: !token
                    ? {}
                    : {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                body: JSON.stringify(sprayProfiles)
            }
        );

        if (response.ok) {
            toast.success('Spray profile updated successfully!');
        } else {
            toast.error(`${response.status} ${response.statusText}`);
        }
        fetchSprayProfiles();
    };

    const openAddSprayProfileDialog = () => {
        setIsAddSprayProfileDialogOpen(true);
    };

    const closeShowAddSprayProfileDialog = () => {
        setIsAddSprayProfileDialogOpen(false);
    };

    const handleAddSprayProfileSuccess = () => {
        setIsAddSprayProfileDialogOpen(false);
        fetchSprayProfiles();
    };

    const openDeleteSprayProfileDialog = (sprayProfile) => {
        setSprayProfileForDeletion(sprayProfile);
        setIsDeleteSprayProfileDialogOpen(true);
    };

    const closeDeleteSprayProfileDialog = () => {
        setSprayProfileForDeletion(null);
        setIsDeleteSprayProfileDialogOpen(false);
    };

    const handleDeleteSprayProfileSuccess = () => {
        closeDeleteSprayProfileDialog();
        fetchSprayProfiles();
    };

    return (
        <>
            <div className={styles.container}>
                <Typography variant="h4" sx={{ pb: 1 }}>
                    {tractor.name}
                </Typography>
                <Typography variant="h5" sx={{ pb: 1 }}>
                    Spray Profile Management
                </Typography>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    alignItems="center"
                >
                    <Box>
                        <Typography>
                            Displays all the spray profiles associated with the sprayer.
                        </Typography>
                    </Box>
                    {haveAnyValuesChanged() ? (
                        <Button
                            variant="outlined"
                            disabled={loading}
                            startIcon={<AddIcon />}
                            onClick={openAddSprayProfileDialog}
                        >
                            Add Spray Profile
                        </Button>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<UndoIcon />}
                                onClick={discardChanges}
                            >
                                Discard Changes
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={updateSprayProfiles}
                                startIcon={<SaveIcon />}
                            >
                                Save Changes
                            </Button>
                        </Box>
                    )}
                </Stack>
                <Stack>
                    <TableContainer component={Paper} sx={{ my: 1 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Total Boom Width (ft)</TableCell>
                                    <TableCell>Max Spray Width (in)</TableCell>
                                    <TableCell>Broadcast App Rate (gpm)</TableCell>
                                    <TableCell>Is Default</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sprayProfiles.map((sprayProfile, index) => (
                                    <TableRow key={sprayProfile.id}>
                                        <TableCell>{index + 1}</TableCell>

                                        <TableCell>
                                            <TextField
                                                type="text"
                                                value={sprayProfile.name}
                                                error={hasSprayProfileValueChanged(sprayProfile, 'name')}
                                                onChange={(e) =>
                                                    onEditSprayProfile(
                                                        sprayProfile.id,
                                                        'name',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <TextField
                                                type="number"
                                                value={sprayProfile.totalBoomWidthFt}
                                                error={hasSprayProfileValueChanged(sprayProfile, 'totalBoomWidthFt')}
                                                onChange={(e) =>
                                                    onEditSprayProfile(
                                                        sprayProfile.id,
                                                        'totalBoomWidthFt',
                                                        e.target.value
                                                    )
                                                }
                                            />

                                        </TableCell>

                                        <TableCell>
                                            <TextField
                                                type="number"
                                                value={sprayProfile.maxSprayWidthIn}
                                                error={hasSprayProfileValueChanged(sprayProfile, 'maxSprayWidthIn')}
                                                onChange={(e) =>
                                                    onEditSprayProfile(
                                                        sprayProfile.id,
                                                        'maxSprayWidthIn',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <TextField
                                                type="number"
                                                value={sprayProfile.broadcastAppRateGpm}
                                                error={hasSprayProfileValueChanged(sprayProfile, 'broadcastAppRateGpm')}
                                                onChange={(e) =>
                                                    onEditSprayProfile(
                                                        sprayProfile.id,
                                                        'broadcastAppRateGpm',
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Checkbox
                                                checked={sprayProfile.isDefault}
                                                onChange={(e) =>
                                                    onEditSprayProfile(
                                                        sprayProfile.id,
                                                        'isDefault',
                                                        e.target.checked)
                                                }
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                startIcon={<DeleteIcon />}
                                                disabled={!haveAnyValuesChanged()}
                                                onClick={() => openDeleteSprayProfileDialog(sprayProfile)}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {addSprayProfileDialogOpen && (
                        <AddSprayProfileDialog
                            clientId={getClientId()}
                            tractorId={getTractorId()}
                            open={addSprayProfileDialogOpen}
                            onClose={closeShowAddSprayProfileDialog}
                            onSuccess={handleAddSprayProfileSuccess}
                        />
                    )}
                    {deleteSprayProfileDialogOpen && (
                        <DeleteSprayProfileDialog
                            clientId={getClientId()}
                            sprayProfileId={sprayProfileForDeletion.id}
                            sprayProfileName={sprayProfileForDeletion.name}
                            open={deleteSprayProfileDialogOpen}
                            onClose={closeDeleteSprayProfileDialog}
                            onSuccess={handleDeleteSprayProfileSuccess}
                        />
                    )}
                </Stack>
            </div>
        </>
    );
};

export default AdminConfigTractorSprayProfiles;
